/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions */
import {
  motion,
  type MotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { useRef, useState } from "react";

import {
  ApplicationArtifact,
  IntroArtifact,
  PracticeArtifact,
  ResumeArtifact,
  StoryIdentity,
} from "./career-story";

const steps = [
  {
    label: "Build",
    title: "Start with a resume that represents you.",
    body: "Create or import your resume. Polish the wording, choose a template, tailor it to a job, and check ATS compatibility with suggestions to improve it.",
    Artifact: ResumeArtifact,
  },
  {
    label: "Show",
    title: "Let recruiters meet the person behind the page.",
    body: "An AI host guides your Spotlight Intro. Tell your story on camera, with a saved transcript and a video recruiters can watch alongside your resume.",
    Artifact: IntroArtifact,
  },
  {
    label: "Practice",
    title: "Practice the conversation, not just the answer.",
    body: "Speak with a realtime AI interviewer. Practice role-specific questions and get feedback on your responses before the interview that matters.",
    Artifact: PracticeArtifact,
  },
  {
    label: "Grow",
    title: "Find a role. Prepare for your next step.",
    body: "Explore jobs and practice for the roles you choose. When a recruiter sends an interview invitation, complete the AI-conducted interview here, too.",
    Artifact: ApplicationArtifact,
  },
];

const JourneyCanvas = ({ progress }: { progress: MotionValue<number> }) => {
  const paperScale = useTransform(progress, [0, 0.17, 0.3, 0.45, 0.55], [1, 1, 0.78, 0.78, 0.65]);
  const paperX = useTransform(progress, [0, 0.18, 0.3, 0.55], ["0%", "0%", "-12%", "-12%"]);
  const paperRotate = useTransform(progress, [0, 0.3, 0.55], [-3, -2, 0]);
  const introOpacity = useTransform(progress, [0.19, 0.3], [0, 1]);
  const introX = useTransform(progress, [0.19, 0.3], ["-34%", "0%"]);
  const introY = useTransform(progress, [0.3, 0.45, 0.55], ["15%", "15%", "0%"]);
  const introScale = useTransform(progress, [0.19, 0.3, 0.45, 0.55], [0.86, 1.12, 1.12, 1]);
  const practiceOpacity = useTransform(progress, [0.44, 0.55], [0, 1]);
  const practiceY = useTransform(progress, [0.44, 0.55], ["20%", "0%"]);
  const practiceScale = useTransform(progress, [0.44, 0.55, 0.7, 0.8], [0.9, 1.05, 1.05, 1]);
  const growthOpacity = useTransform(progress, [0.69, 0.8], [0, 1]);
  const growthY = useTransform(progress, [0.69, 0.8], ["24%", "0%"]);
  const connection = useTransform(progress, [0.77, 0.97], [0, 1]);

  return (
    <div className="journey-canvas" aria-hidden="true">
      <motion.div
        className="journey-paper"
        style={{ scale: paperScale, x: paperX, rotate: paperRotate }}
      >
        <ResumeArtifact />
      </motion.div>
      <motion.div
        className="journey-intro"
        style={{ opacity: introOpacity, x: introX, y: introY, scale: introScale }}
      >
        <IntroArtifact />
      </motion.div>
      <motion.div
        className="journey-practice"
        style={{ opacity: practiceOpacity, y: practiceY, scale: practiceScale }}
      >
        <PracticeArtifact />
      </motion.div>
      <motion.div className="journey-application" style={{ opacity: growthOpacity, y: growthY }}>
        <ApplicationArtifact />
      </motion.div>
      <svg className="journey-return" viewBox="0 0 600 520" fill="none">
        <motion.path
          d="M470 445 V488 Q470 503 455 503 H55 Q40 503 40 488 V440"
          stroke="currentColor"
          strokeWidth="1.4"
          style={{ pathLength: connection }}
        />
        <motion.path
          d="m35 448 5-9 5 9"
          stroke="currentColor"
          strokeWidth="1.4"
          style={{ opacity: connection }}
        />
      </svg>
      <motion.p className="journey-return-label" style={{ opacity: connection }}>
        The learning comes with you.
      </motion.p>
    </div>
  );
};

export const IdentitySection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (value) => {
    const next = Math.min(3, Math.max(0, Math.floor(value * 4)));
    setActiveStep((current) => (current === next ? current : next));
  });

  const goToStep = (index: number) => {
    const section = sectionRef.current;
    if (!section) return;
    const start = section.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: start + (section.offsetHeight - window.innerHeight) * (index / 4 + 0.08),
      behavior: "auto",
    });
  };

  return (
    <section
      id="identity"
      ref={sectionRef}
      className={`folio-journey${reduced ? " folio-journey-static" : ""}`}
    >
      <div className="folio-shell journey-stage">
        <div className="journey-copy">
          <p className="folio-eyebrow">One person. A fuller picture.</p>
          <h2>A professional identity that unfolds.</h2>
          <div className="journey-desktop-copy">
            <nav className="journey-rail" aria-label="Explore the four stages">
              {steps.map((step, index) => (
                <button
                  type="button"
                  key={step.label}
                  aria-current={activeStep === index ? "step" : undefined}
                  onClick={() => goToStep(index)}
                  data-complete={index < activeStep}
                >
                  <span className="journey-rail-dot" />
                  <small>0{index + 1}</small>
                  {step.label}
                </button>
              ))}
            </nav>
            <div className="journey-active-copy">
              {steps.map((step, index) => (
                <article
                  key={step.label}
                  aria-hidden={activeStep !== index}
                  className={activeStep === index ? "is-active" : ""}
                >
                  <p className="folio-label">{step.label}</p>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </article>
              ))}
            </div>
          </div>
          <div className="journey-desktop-identity">
            <StoryIdentity detail="One example, from beginning to next step" />
          </div>
        </div>
        <div className="journey-desktop-visual">
          <JourneyCanvas progress={scrollYProgress} />
        </div>
        <div className="journey-mobile-chapters">
          {steps.map((step, index) => (
            <article key={step.label} className="journey-mobile-chapter">
              <div className="journey-mobile-heading">
                <span>0{index + 1}</span>
                <p className="folio-label">{step.label}</p>
              </div>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
              <motion.div
                className="journey-mobile-artifact"
                initial={reduced ? false : { opacity: 0.65, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5 }}
              >
                <step.Artifact />
              </motion.div>
            </article>
          ))}
          <StoryIdentity detail="An illustrative story. A fuller picture." />
        </div>
      </div>
    </section>
  );
};
